import {
  addMonths,
  differenceInMonths,
  endOfMonth,
  isBefore,
  subMonths,
} from 'date-fns';
import _ from 'lodash';
import { Dateish } from '../enums/utilities';
import { PriceWhole } from '../interfaces/database/price';
import { TransactionWhole } from '../interfaces/database/transaction';
import { AsOfValue } from '../interfaces/value-helper';
import { DateUtil } from './date';

export class ValueHelper {
  static findPrice(prices: PriceWhole[], asOf: Date): PriceWhole | undefined {
    // only look at prices effective before the asOf date
    const pricesBefore = prices.filter((p) => {
      const priceDate = DateUtil.castDate(p.effective);

      if (!priceDate) {
        // ignore empty dates
        return false;
      }

      return isBefore(priceDate, asOf);
    });

    // use the most recent price, if any
    return _.maxBy(pricesBefore, (p) => p.effective);
  }

  static getTrxValues(
    trx: TransactionWhole,
    prices: PriceWhole[],
    upTo: Dateish
  ): AsOfValue[] {
    try {
      if (!trx.settleDate) {
        throw new Error('Invalid transaction settleDate');
      }

      const upToDate = DateUtil.castDate(upTo);

      if (!upToDate) {
        throw new Error('Invalid upTo date');
      }

      const startDate = endOfMonth(trx.settleDate);
      const endDate = endOfMonth(subMonths(upToDate, 1));
      const months = differenceInMonths(endDate, startDate);

      const output: AsOfValue[] = [];

      for (let i = 0; i < months; i++) {
        output.push(
          ValueHelper.getTrxValue(trx, prices, addMonths(startDate, i))
        );
      }

      return output;
    } catch (e) {
      console.error('getTrxValues', e);
      return [];
    }
  }

  static getTrxValue(
    trx: TransactionWhole,
    prices: PriceWhole[],
    asOf: Date
  ): AsOfValue {
    if (!trx.settleDate || isBefore(asOf, trx.settleDate)) {
      // trx isn't worth anything before it is settled
      return {
        amount: 0,
        asOf: asOf,
      };
    }

    if (trx.units) {
      // use the most recent price, if any
      const price = ValueHelper.findPrice(prices, asOf);

      if (price) {
        return {
          amount: trx.units * price?.pps,
          asOf: asOf,
        };
      }
    }

    // todo: confirm when no units and/or price => fallback to 0 or something else?
    return {
      amount: 0,
      asOf: asOf,
    };
  }

  static getTrxDividend(trx: TransactionWhole, eom: Date): AsOfValue {
    if (!trx.settleDate || isBefore(eom, trx.settleDate)) {
      return {
        amount: 0,
        asOf: eom,
      };
    }

    // todo: logic based on trx amount, dividend type, etc...

    return {
      amount: 0,
      asOf: eom,
    };
  }

  static getTrxDividends(trx: TransactionWhole, upTo: Dateish): AsOfValue[] {
    try {
      if (!trx.settleDate) {
        throw new Error('Invalid transaction settleDate');
      }

      const upToDate = DateUtil.castDate(upTo);

      if (!upToDate) {
        throw new Error('Invalid upTo date');
      }

      const startDate = endOfMonth(trx.settleDate);
      const endDate = endOfMonth(subMonths(upToDate, 1));
      const months = differenceInMonths(endDate, startDate);

      const output: AsOfValue[] = [];

      for (let i = 0; i < months; i++) {
        output.push(ValueHelper.getTrxDividend(trx, addMonths(startDate, i)));
      }

      return output;
    } catch (e) {
      console.error('getTrxDividends', e);
      return [];
    }
  }
}
